import { Injectable, computed } from '@angular/core';
import { patchState, signalState } from '@ngrx/signals';

import { TvoedCalculatorState, initialTvoedCalculatorState } from '../../models/tvoed-calculator-state.model';
import { TvoedComputedExtraPayment, ZulageDetail } from '../../models/tvoed-extra-payment.model';
import { TvoedSubTableData } from '../../models/tvoed-data.model';

/** Average weeks per month (52 / 12) */
const WEEKS_PER_MONTH = 4.348;

@Injectable({
  providedIn: 'root'
})
export class TvoedCalculatorService {
  state = signalState<TvoedCalculatorState>(initialTvoedCalculatorState);

  updateState(partial: Partial<TvoedCalculatorState>) {
    patchState(this.state, partial);
  }

  // --- Computed Signals for Calculation Logic ---

  baseSalary = computed(() => {
    const s = this.state;
    const tvoedData = s.tvoedData();
    if (!tvoedData || !s.subTable() || !s.payGrade() || !s.payGradeStep()) return 0;
    return tvoedData[s.subTable()]?.data[s.payGrade()]?.[s.payGradeStep()] ?? 0;
  });

  workingHoursFactor = computed(() => {
    const s = this.state;
    if (s.weeklyWorkingHours() === 0) return 1;
    return (s.weeklyWorkingHours() + s.weeklyAdditionalWorkingHours()) / s.weeklyWorkingHours();
  });

  extraHoursSalary = computed(() => {
    const baseMonth = this.baseSalary();
    return baseMonth * this.workingHoursFactor() - baseMonth;
  });

  monthlySalary = computed(() => {
    return this.baseSalary() * this.workingHoursFactor();
  });

  /** Hourly rate based on base salary and standard weekly hours */
  hourlyRate = computed(() => {
    const s = this.state;
    const weeklyHours = s.weeklyWorkingHours();
    if (weeklyHours === 0) return 0;
    return this.baseSalary() / (weeklyHours * WEEKS_PER_MONTH);
  });

  /** Calculate individual Zulagen (optional supplements) based on TVöD § 8 */
  zulagenDetails = computed((): ZulageDetail[] => {
    const s = this.state;
    const base = this.baseSalary();
    if (base === 0) return [];
    const rate = this.hourlyRate();
    const tvoedData = s.tvoedData();
    const subTableData = tvoedData?.[s.subTable()];
    const details: ZulageDetail[] = [];

    // Wechselschichtzulage (§ 8 Abs. 5 TVöD): fixed monthly amount from data
    if (s.zulagenSchicht()) {
      const amount = subTableData?.wechselschichtzulage ?? 200;
      details.push({
        name: 'Wechselschichtzulage',
        amount,
        info: `Ständige Wechselschichtzulage gem. § 8 Abs. 5 TVöD: ${amount}€/Monat`
      });
    }

    // Nachtzuschlag (§ 8 Abs. 1 Satz 2a TVöD): 20% des Stundenentgelts pro Nachtstunde
    // Schätzung: ~40 Nachtstunden/Monat (typisch für Schichtarbeit)
    if (s.zulagenNacht()) {
      const estimatedNightHours = 40;
      const amount = rate * 0.2 * estimatedNightHours;
      details.push({
        name: 'Nachtzuschlag',
        amount,
        info: `20% des Stundenentgelts (${this.formatCurrency(
          rate
        )}) × geschätzte ${estimatedNightHours} Nachtstunden/Monat. Tatsächlicher Betrag hängt von den geleisteten Nachtstunden ab (§ 8 Abs. 1 TVöD).`
      });
    }

    // Samstags-/Sonntags-/Feiertagszuschlag (§ 8 Abs. 1 TVöD)
    // Samstag 13-21 Uhr: 20%, Sonntag: 25%, Feiertag: 35% (mit Freizeitausgleich)
    // Schätzung: ~20 Wochenendstunden/Monat mit durchschnittlich 25%
    if (s.zulagenWochenende()) {
      const estimatedWeekendHours = 20;
      const amount = rate * 0.25 * estimatedWeekendHours;
      details.push({
        name: 'Wochenend-/Feiertagszuschlag',
        amount,
        info: `25% des Stundenentgelts (${this.formatCurrency(
          rate
        )}) × geschätzte ${estimatedWeekendHours} Wochenendstunden/Monat. Sa 13-21 Uhr: 20%, So: 25%, Feiertag: 35-135% (§ 8 Abs. 1 TVöD).`
      });
    }

    // Überstundenzuschlag (§ 8 Abs. 1 TVöD)
    // VKA: 30% (E 1-E 9b), Bund: 15% (E 1-E 9b)
    if (s.zulagenUeberstunden()) {
      const additionalHours = s.weeklyAdditionalWorkingHours();
      if (additionalHours > 0) {
        const zuschlagRate = subTableData?.ueberstundenzuschlag ?? 0.30;
        const zuschlagPercent = Math.round(zuschlagRate * 100);
        const monthlyOvertimeHours = additionalHours * WEEKS_PER_MONTH;
        const amount = rate * zuschlagRate * monthlyOvertimeHours;
        details.push({
          name: 'Überstundenzuschlag',
          amount,
          info: `${zuschlagPercent}% des Stundenentgelts (${this.formatCurrency(
            rate
          )}) × ${additionalHours} Überstunden/Woche × ${WEEKS_PER_MONTH} Wochen/Monat. Gilt für E 1-E 9b (§ 8 Abs. 1 TVöD). Ab E 10 i.d.R. kein Überstundenzuschlag.`
        });
      }
    }

    // Funktionszulage: pauschaler Betrag für besondere Funktionen/Verantwortung
    if (s.zulagenFunktion()) {
      details.push({
        name: 'Funktionszulage',
        amount: 100,
        info: 'Pauschale Funktionszulage (z.B. Vorarbeiter, besondere Verantwortung). Der tatsächliche Betrag kann je nach Arbeitgeber variieren.'
      });
    }

    return details;
  });

  /** Total of all active Zulagen */
  zulagenTotal = computed(() => {
    return this.zulagenDetails().reduce((sum, z) => sum + z.amount, 0);
  });

  /** Determine Jahressonderzahlung factor based on pay grade */
  jahressonderzahlungFactor = computed(() => {
    const s = this.state;
    const tvoedData = s.tvoedData();
    if (!tvoedData || !s.subTable() || !s.payGrade()) return 0;
    const subTableData = tvoedData[s.subTable()];
    if (!subTableData?.jahressonderzahlung) return 0;

    const payGrade = s.payGrade();
    const jsz = subTableData.jahressonderzahlung;

    // Find matching bracket for the pay grade
    for (const [key, factor] of Object.entries(jsz)) {
      if (this.payGradeMatchesBracket(payGrade, key)) {
        return factor as number;
      }
    }
    return 0;
  });

  extraPayments = computed(() => {
    const s = this.state;
    const extraPaymentData = s.extraPaymentData();
    if (!s.tvoedData() || !s.subTable() || !extraPaymentData) return [];

    return extraPaymentData.map(extraPayment => {
      let money = 0;
      let info = extraPayment.info;

      if (extraPayment.useJahressonderzahlung) {
        // Jahressonderzahlung: percentage of base salary (not including overtime)
        const factor = this.jahressonderzahlungFactor();
        money = this.baseSalary() * this.workingHoursFactor() * factor;
        info = info.replace('{{factorPercent}}', String(Math.round(factor * 10000) / 100));
      } else if (extraPayment.calculateWith === 'absolute') {
        // Leistungsentgelt (§ 18 TVöD): absolute EUR amount entered by user
        money = s.leistungsentgeltAmount();
      } else if (extraPayment.calculateWith === 'monthlySalary') {
        // Percentage of base salary
        money = this.baseSalary() * this.workingHoursFactor() * (extraPayment.factor ?? 0) * 12;
        info = info.replace('{{factorPercent}}', String((extraPayment.factor ?? 0) * 100));
      } else {
        money = this.baseSalary() * (extraPayment.factor ?? 0);
      }

      return {
        name: extraPayment.name,
        paymentMonth: extraPayment.paymentMonth,
        money,
        info
      };
    });
  });

  totalAnnualSalary = computed(() => {
    const s = this.state;
    let total = (this.monthlySalary() + this.zulagenTotal()) * 12;
    this.extraPayments().forEach((ep: TvoedComputedExtraPayment) => (total += ep.money));
    total += s.additionalExtraPayment();
    return total;
  });

  totalMonthlySalary = computed(() => {
    return this.totalAnnualSalary() / 12;
  });

  /** Hourly rate based on total monthly salary per TVöD § 24 Abs. 3 */
  totalHourlySalary = computed(() => {
    const totalHours = this.state.weeklyWorkingHours() + this.state.weeklyAdditionalWorkingHours();
    if (totalHours === 0) return 0;
    return this.totalMonthlySalary() / (WEEKS_PER_MONTH * totalHours);
  });

  generatedUrl = computed(() => {
    const s = this.state;
    const params = [
      `labourAgreementId=${s.labourAgreementId()}`,
      `subTable=${s.subTable()}`,
      `payGrade=${s.payGrade()}`,
      `payGradeStep=${s.payGradeStep()}`,
      `weeklyWorkingHours=${s.weeklyWorkingHours()}`,
      `weeklyAdditionalWorkingHours=${s.weeklyAdditionalWorkingHours()}`,
      `additionalExtraPayment=${s.additionalExtraPayment()}`,
      `zulagenSchicht=${s.zulagenSchicht()}`,
      `zulagenNacht=${s.zulagenNacht()}`,
      `zulagenWochenende=${s.zulagenWochenende()}`,
      `zulagenUeberstunden=${s.zulagenUeberstunden()}`,
      `zulagenFunktion=${s.zulagenFunktion()}`,
      `leistungsentgeltAmount=${s.leistungsentgeltAmount()}`
    ];
    return encodeURI(`https://www.tvoedgehalt.de/gehalt/rechner?${params.join('&')}`);
  });

  // --- Computed Properties for the UI ---

  chartData = computed(() => ({
    monthlyBaseSalary: this.baseSalary(),
    extraHoursSalary: this.extraHoursSalary(),
    zulagenTotal: this.zulagenTotal(),
    zulagenDetails: this.zulagenDetails(),
    extraPayments: this.extraPayments()
  }));

  subTables = computed(() => {
    const s = this.state;
    const tvoedData = s.tvoedData();
    if (tvoedData) {
      return Object.entries(tvoedData).map(([key, subTableData]: [string, TvoedSubTableData]) => ({
        key,
        name: subTableData.name
      }));
    }
    return [];
  });

  payGrades = computed(() => {
    const s = this.state;
    const tvoedData = s.tvoedData();
    if (tvoedData && s.subTable() && tvoedData[s.subTable()]) {
      return Object.keys(tvoedData[s.subTable()].data) || [];
    }
    return [];
  });

  payGradeSteps = computed(() => {
    const s = this.state;
    const tvoedData = s.tvoedData();
    if (tvoedData && s.subTable() && s.payGrade() && tvoedData[s.subTable()]?.data[s.payGrade()]) {
      return Object.keys(tvoedData[s.subTable()].data[s.payGrade()]) || [];
    }
    return [];
  });

  /** Get the weekly working hours for the currently selected sub-table */
  getDefaultWorkingHours(): number {
    const tvoedData = this.state.tvoedData();
    if (tvoedData && this.state.subTable()) {
      return tvoedData[this.state.subTable()]?.weeklyWorkingHours ?? 39;
    }
    return 39;
  }

  compareWith = (id1: string | number, id2: string | number): boolean => `${id1}` === `${id2}`;

  // --- Private helpers ---

  private formatCurrency(value: number): string {
    return value.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
  }

  /**
   * Check if a pay grade matches a bracket range key like "E 1-E 8", "S 2-S 18", "P 5-P 16"
   */
  private payGradeMatchesBracket(payGrade: string, bracketKey: string): boolean {
    // Parse bracket: "E 1-E 8", "S 2-S 18", "E 9a-E 12", "P 5-P 16"
    const parts = bracketKey.split('-').map(p => p.trim());
    if (parts.length !== 2) return false;

    const prefix = this.extractPrefix(payGrade);
    const prefixFrom = this.extractPrefix(parts[0]);
    const prefixTo = this.extractPrefix(parts[1]);

    // Prefix must match
    if (prefix !== prefixFrom || prefix !== prefixTo) return false;

    const gradeNum = this.extractGradeNumber(payGrade);
    const fromNum = this.extractGradeNumber(parts[0]);
    const toNum = this.extractGradeNumber(parts[1]);

    return gradeNum >= fromNum && gradeNum <= toNum;
  }

  private extractPrefix(grade: string): string {
    // "E 12" -> "E", "S 8a" -> "S", "P 7" -> "P", "E 15Ü" -> "E"
    const match = grade.match(/^([A-Z])\s/);
    return match ? match[1] : grade[0];
  }

  private extractGradeNumber(grade: string): number {
    // "E 12" -> 12, "E 9a" -> 9, "E 15Ü" -> 15, "E 2Ü" -> 2
    const match = grade.match(/(\d+)/);
    return match ? parseInt(match[1], 10) : 0;
  }
}
